import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userApi } from '../services/api';

interface SecretKeyData {
  secret_key: string;
  created_at: string;
  last_used: string | null;
  usage_info: string;
  notice?: string;
}

interface SecretKeyManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const SecretKeyManager: React.FC<SecretKeyManagerProps> = ({ isOpen, onClose }) => {
  const { isAuthenticated } = useAuth();
  const [secretKeyData, setSecretKeyData] = useState<SecretKeyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);


  // 阻止背景滚动和键盘事件处理
  useEffect(() => {
    if (isOpen) {
      // 保存当前滚动位置
      const scrollY = window.scrollY;
      
      // 阻止背景滚动
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      
      // 添加Escape键监听
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        // 恢复背景滚动
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        
        // 恢复滚动位置
        window.scrollTo(0, scrollY);
        
        // 移除键盘监听
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  // 获取用户的Secret Key
  const fetchSecretKey = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await userApi.getSecretKey();
      
      if (response.success) {
        setSecretKeyData(response.data);
      } else {
        throw new Error(response.message || '获取Secret Key失败');
      }
    } catch (err: any) {
      console.error('获取Secret Key失败:', err);
      setError(err.message || '获取Secret Key失败');
    } finally {
      setLoading(false);
    }
  };

  // 刷新Secret Key
  const refreshSecretKey = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      const response = await userApi.refreshSecretKey();
      
      if (response.success) {
        setSecretKeyData(response.data);
        setShowKey(true);
        alert('Secret Key已刷新！请更新您的MCP配置。');
      } else {
        throw new Error(response.message || '刷新Secret Key失败');
      }
    } catch (err: any) {
      console.error('刷新Secret Key失败:', err);
      setError(err.message || '刷新Secret Key失败');
    } finally {
      setRefreshing(false);
    }
  };

  // 复制到剪贴板
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };



  useEffect(() => {
    if (isAuthenticated && isOpen) {
      fetchSecretKey();
    }
  }, [isAuthenticated, isOpen]);

  // 如果模态框未打开，不渲染任何内容
  if (!isOpen) {
    return null;
  }

  // 渲染主模态框内容
  const renderContent = () => {
    if (!isAuthenticated) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">请先登录以管理您的Secret Key</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 dark:border-blue-400"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">加载中...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 transition-colors duration-200">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400 dark:text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="text-red-800 dark:text-red-300">{error}</span>
          </div>
          <button
            onClick={fetchSecretKey}
            className="mt-2 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-md hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
          >
            重试
          </button>
        </div>
      );
    }

    if (!secretKeyData) {
      return null;
    }

    return (
      <>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Secret Key 管理</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Secret Key用于MCP服务器认证，确保只有您可以访问自己的私有提示词。
          </p>
        </div>

        <div className="space-y-6">
          {/* Secret Key 信息 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
              <svg className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              您的Secret Key
            </h3>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md transition-colors duration-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Secret Key:
                </label>
                <button
                  onClick={() => setShowKey(!showKey)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  {showKey ? '隐藏' : '显示'}
                </button>
              </div>
              
              <div className="flex items-center space-x-2">
                <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded border border-gray-300 dark:border-gray-600 text-sm font-mono text-gray-900 dark:text-gray-100">
                  {showKey ? secretKeyData.secret_key : '•'.repeat(64)}
                </code>
                <button
                  onClick={() => copyToClipboard(secretKeyData.secret_key)}
                  className="px-3 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors text-sm"
                >
                  {copied ? '已复制!' : '复制'}
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">创建时间:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  {new Date(secretKeyData.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">最后使用:</span>
                <p className="text-gray-600 dark:text-gray-400">
                  {secretKeyData.last_used 
                    ? new Date(secretKeyData.last_used).toLocaleString() 
                    : '从未使用'}
                </p>
              </div>
            </div>

            {secretKeyData.notice && (
              <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-md">
                <p className="text-amber-800 dark:text-amber-300 text-sm">{secretKeyData.notice}</p>
              </div>
            )}
          </div>

          {/* 操作按钮 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">操作</h3>
            <div className="space-y-4">
              <button
                onClick={refreshSecretKey}
                disabled={refreshing}
                className="w-full md:w-auto px-4 py-2 bg-orange-500 dark:bg-orange-600 text-white rounded-md hover:bg-orange-600 dark:hover:bg-orange-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {refreshing ? '刷新中...' : '刷新Secret Key'}
              </button>
              
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ⚠️ 刷新Secret Key会使旧的密钥立即失效，您需要在MCP配置中更新新密钥。
              </p>
            </div>
          </div>

          {/* MCP配置 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
            <h3 className="text-lg font-semibold mb-4 flex items-center text-gray-900 dark:text-white">
              <svg className="h-5 w-5 mr-2 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
              </svg>
              MCP配置
            </h3>
            
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 transition-colors duration-200">
              <div className="flex items-start space-x-3">
                <svg className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">推荐使用 Smithery 一键配置</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                    访问 Smithery 官方页面，一键生成您的MCP配置，无需手动编写复杂配置文件。
                  </p>
                  <a
                    href="https://smithery.ai/server/@vines90/mcp-prompt-server"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-md hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    前往 Smithery 配置
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* 使用说明 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-colors duration-200">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">使用说明</h3>
            <div className="space-y-4 text-sm">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 dark:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  1
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">进入 Smithery 主页</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    访问 
                    <a 
                      href="https://smithery.ai/server/@vines90/mcp-prompt-server" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline mx-1"
                    >
                      https://smithery.ai/server/@vines90/mcp-prompt-server
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 dark:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  2
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">选择配置方式并输入 Secret Key</p>
                  <p className="text-gray-600 dark:text-gray-400">在页面上选择适合的配置方式（Cursor、Claude Desktop等），并输入您的 Secret Key</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-blue-500 dark:bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  3
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">点击 Connect 生成配置</p>
                  <p className="text-gray-600 dark:text-gray-400">点击 Connect 按钮即可自动生成完整的配置信息，复制到您的编辑器配置文件中即可</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {/* 主模态框 */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-8"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-6xl max-h-[85vh] flex flex-col transition-colors duration-200">
          {/* 头部 - 固定 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Secret Key 管理</h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* 内容区域 - 可滚动 */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-6">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>


    </>
  );
};

export default SecretKeyManager; 